import { getOfferPackageName, getPackageNumberOfNights, getPackagePersistId } from '../PackageSetupStep/utils';
import { getRatePlanDraftLabel } from '../RatePlanStep/utils';
import type { FlashRatePlanDraft } from '../RatePlanStep/utils';
import type { RoomRateRoom } from '../RoomRatesStep/utils';
import { getCreatableRoomDrafts } from '../RoomsStep/utils';
import type { FlashOfferRoomDraft } from '../RoomsStep/utils';
import type { TaxesAndFeesUpdatePayload } from '../TaxesFeesStep/formData';
import { taxFeeKey } from '../TaxesFeesStep/taxRules';
import type { FlashOfferProperty } from '../types';

export type DraftEntryStatus = 'error' | 'pending' | 'success';

export interface DraftSummaryDetail {
  label: string;
  value: string;
}

export interface DraftSummaryItem {
  details: Array<DraftSummaryDetail>;
  id?: string;
  primary: string;
  /** A one-line recap shown beside the title, e.g. the room a package will link to. */
  secondary?: string;
  status?: DraftEntryStatus;
}

export interface DraftSummarySection {
  items: Array<DraftSummaryItem>;
  key: SubmissionSectionKey;
  status?: DraftEntryStatus;
  summary: string;
  title: string;
}

export type SubmissionSectionKey = 'packages' | 'property' | 'ratePlans' | 'roomRates' | 'rooms' | 'taxesFees';

export interface DraftSummary {
  packages: DraftSummarySection;
  property: DraftSummarySection;
  ratePlans: DraftSummarySection;
  roomRates: DraftSummarySection;
  rooms: DraftSummarySection;
  taxesFees: DraftSummarySection;
}

export interface BuildDraftSummaryParams {
  flexibleNightsById: Record<string, boolean>;
  matchedPackages: Array<App.Package>;
  packageRoomTypeIds: Record<string, string>;
  property: FlashOfferProperty | null;
  ratePlanDrafts: Array<FlashRatePlanDraft>;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomRateRooms: Array<RoomRateRoom>;
  selectedRoomRatePlanIds: Record<string, Array<string>>;
  taxesFeesDraft: TaxesAndFeesUpdatePayload | null;
}

function pluralize(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function yesNo(value: boolean | undefined) {
  return value ? 'Yes' : 'No';
}

/**
 * Assembles a read-only, per-entity summary of everything the CM has drafted, for the
 * Submission step's nested review accordions (one per section, one per item within a section).
 * Purely presentational data -- no ids are resolved here.
 */
export function buildDraftSummary({
  flexibleNightsById,
  matchedPackages,
  packageRoomTypeIds,
  property,
  ratePlanDrafts,
  roomDrafts,
  roomRateRooms,
  selectedRoomRatePlanIds,
  taxesFeesDraft,
}: BuildDraftSummaryParams): DraftSummary {
  const creatableRoomDrafts = getCreatableRoomDrafts(roomDrafts);
  const newRoomKeys = new Set(creatableRoomDrafts.map((draft) => draft.draftId));
  const roomRateEntries = Object.entries(selectedRoomRatePlanIds);
  const roomRateAssignmentCount = roomRateEntries.reduce((total, [, ratePlanIds]) => total + ratePlanIds.length, 0);
  const taxFeeItems = taxesFeesDraft?.taxes_and_fees ?? [];

  return {
    property: {
      items: property
        ? [
            {
              details: [
                { label: 'Category', value: property.category || 'Unknown' },
                { label: 'Channel manager', value: property.channel_manager || 'Unknown' },
                ...(property.hotel_code ? [{ label: 'Hotel code', value: property.hotel_code }] : []),
                { label: 'Property id', value: property.id },
              ],
              primary: property.name,
            },
          ]
        : [],
      key: 'property',
      summary: property?.name || 'Untitled property',
      title: 'Property',
    },
    rooms: {
      items: creatableRoomDrafts.map((draft) => ({
        details: [
          { label: 'Description', value: draft.description || 'None' },
          { label: 'Category', value: draft.category || 'Unknown' },
          { label: 'Size (sqm)', value: draft.size_sqm ? String(draft.size_sqm) : 'Not set' },
          { label: 'Bedrooms', value: draft.number_of_bedrooms ? String(draft.number_of_bedrooms) : 'Not set' },
          { label: 'Bathrooms', value: draft.number_of_bathrooms ? String(draft.number_of_bathrooms) : 'Not set' },
          {
            label: 'Amenities',
            value:
              draft.amenities
                ?.map((amenity) => (typeof amenity === 'string' ? amenity : amenity.name))
                .filter((name): name is string => !!name)
                .join(', ') || 'None',
          },
        ],
        primary: draft.name || 'Untitled room',
      })),
      key: 'rooms',
      summary: pluralize(creatableRoomDrafts.length, 'room'),
      title: 'Rooms to create',
    },
    ratePlans: {
      items: ratePlanDrafts.map((draft, index) => ({
        details: [
          { label: 'Cancellation policy', value: draft.ratePlanCancellationPolicy || 'None' },
          { label: 'Status', value: draft.ratePlanStatus },
          { label: 'Rate type', value: draft.ratePlanRateType },
        ],
        primary: getRatePlanDraftLabel(draft, index),
      })),
      key: 'ratePlans',
      summary: pluralize(ratePlanDrafts.length, 'rate plan'),
      title: 'Rate plans to create',
    },
    roomRates: {
      items: roomRateEntries.map(([roomKey, ratePlanIds]) => {
        const ratePlanNames = ratePlanIds
          .map((ratePlanId) => ratePlanDrafts.find((draft) => draft.draftId === ratePlanId)?.ratePlanName)
          .filter((name): name is string => !!name);

        return {
          details: ratePlanNames.map((name) => ({ label: 'Rate plan', value: name })),
          primary: roomRateRooms.find((room) => room.id === roomKey)?.name ?? roomKey,
        };
      }),
      key: 'roomRates',
      summary: `${roomRateAssignmentCount} across ${pluralize(roomRateEntries.length, 'room')}`,
      title: 'Room rate assignments',
    },
    taxesFees: {
      items: taxFeeItems.map((item) => ({
        details: [
          { label: 'Type', value: item.type === 'stay' ? 'Per stay' : 'Per night' },
          { label: 'Per person', value: yesNo(item.per_person) },
          { label: 'Payable at property', value: yesNo(item.payable_at_property) },
          { label: 'Additional tax (CM)', value: yesNo(item.additional_tax) },
          { label: 'Excluded from CM rate', value: yesNo(item.exclude_from_cm_rate) },
        ],
        primary: taxFeeKey(item),
      })),
      key: 'taxesFees',
      summary: taxesFeesDraft ? pluralize(taxFeeItems.length, 'tax/fee') : 'Not set',
      title: 'Taxes & fees',
    },
    packages: {
      items: matchedPackages.map((pkg) => {
        const packageId = getPackagePersistId(pkg);
        const numberOfNights = getPackageNumberOfNights(pkg);
        const roomKey = packageId ? packageRoomTypeIds[packageId] : undefined;
        const roomName = roomRateRooms.find((room) => room.id === roomKey)?.name;
        const newRoomSuffix = roomKey && newRoomKeys.has(roomKey) ? ' (new room)' : '';

        return {
          details: [
            { label: 'Nights', value: numberOfNights !== undefined ? String(numberOfNights) : 'Not set' },
            { label: 'Flexible nights', value: yesNo(packageId ? flexibleNightsById[packageId] : false) },
          ],
          id: packageId,
          primary: getOfferPackageName(pkg),
          secondary: roomName ? `${roomName}${newRoomSuffix}` : 'Room not mapped',
        };
      }),
      key: 'packages',
      summary: pluralize(matchedPackages.length, 'package'),
      title: 'Packages to link',
    },
  };
}

const SECTION_ORDER: Array<SubmissionSectionKey> = [
  'property',
  'rooms',
  'ratePlans',
  'roomRates',
  'taxesFees',
  'packages',
];

/**
 * Every section's failure originates from -- and its "Go to step" CTA should return to -- the
 * wizard step that owns that draft data, per `constants.ts`'s STEPS order. Notably both package
 * sub-failures (flexible-nights save and real package-linking) map to the same Package Setup step
 * (8), since that's the one step where a CM edits either of those things.
 */
export const SECTION_STEP_INDEX: Record<SubmissionSectionKey, number> = {
  packages: 8,
  property: 2,
  ratePlans: 4,
  roomRates: 5,
  rooms: 3,
  taxesFees: 7,
};

export interface ApplySubmissionStatusParams {
  failedStage: SubmissionSectionKey | null;
  hasSucceeded: boolean;
  packageLinkFailures: Array<string>;
}

/**
 * Overlays a completed (or failed) submission attempt's outcome onto an otherwise-plain draft
 * summary: every section up to and including the one that failed gets a pass/fail status, so the
 * review accordions can flag exactly which entity needs attention. Sections after the failure
 * point are never reached and stay unannotated (neutral, no chip). Package-linking failures name
 * the specific packages that didn't match, so those items get their own error status instead of
 * the whole section going red uniformly.
 */
export function applySubmissionStatus(
  summary: DraftSummary,
  { failedStage, hasSucceeded, packageLinkFailures }: ApplySubmissionStatusParams,
): DraftSummary {
  if (!hasSucceeded && !failedStage) {
    return summary;
  }

  const failedIndex = failedStage ? SECTION_ORDER.indexOf(failedStage) : -1;

  const annotated = { ...summary };

  SECTION_ORDER.forEach((key) => {
    const section = annotated[key];
    const sectionIndex = SECTION_ORDER.indexOf(key);
    const isFailedSection = sectionIndex === failedIndex;
    const isPastSection = failedIndex === -1 || sectionIndex < failedIndex;

    if (!isPastSection && !isFailedSection) {
      return;
    }

    const status: DraftEntryStatus = isFailedSection ? 'error' : 'success';
    const items = section.items.map((item) => {
      if (!isFailedSection) {
        return { ...item, status: 'success' as const };
      }

      if (key === 'packages' && packageLinkFailures.length > 0) {
        const itemFailed = packageLinkFailures.some((failure) => failure.includes(item.primary));
        return { ...item, status: itemFailed ? ('error' as const) : ('success' as const) };
      }

      return { ...item, status: 'error' as const };
    });

    annotated[key] = { ...section, items, status };
  });

  return annotated;
}

export function getSubmissionBannerMessage(
  submitErrorMessage: string | null,
  isFailureActionable: boolean,
  hasUnresolvedRollbackErrors: boolean,
) {
  if (!submitErrorMessage) {
    return 'Rollback is automatic in case any resource creation fails.';
  }

  if (hasUnresolvedRollbackErrors) {
    return "Some cleanup after this failure didn't complete -- a created resource may still exist. Creating the offer again is disabled until support confirms it's clear.";
  }

  const nextSteps = isFailureActionable
    ? 'Fix the issue below, then try again.'
    : "This couldn't be resolved from within the wizard -- see the error below and share it with support.";

  return `Everything created during this attempt (other than the property) has been rolled back. ${nextSteps}`;
}
